import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import React, { useEffect } from 'react';
import { GameMode } from '@/lib/types/common/GameMode';
import { GameModeAdvancedSettings } from '@/lib/types/common/GameModeAdvancedSettings';
import { GameModeSettingsProvider } from '@/app/providers/GameModeSettingsProvider';
import ClassicModeAdvancedSettings from './AdvancedSettings/ClassicModeAdvancedSettings';
import TimedModeAdvancedSettings from './AdvancedSettings/TimedModeAdvancedSettings';
import EndlessModeAdvancedSettings from './AdvancedSettings/EndlessModeAdvancedSettings';
import FlashModeAdvancedSettings from './AdvancedSettings/FlashModeAdvancedSettings';
import HazardModeAdvancedSettings from './AdvancedSettings/HazardModeAdvancedSettings';
import DailyModeAdvancedSettings from './AdvancedSettings/DailyModeAdvancedSettings';
import FightModeAdvancedSettings from './AdvancedSettings/FightModeAdvancedSettings';
import CoopModeAdvancedSettings from './AdvancedSettings/CoopModeAdvancedSettings';
import CustomModeAdvancedSettings from './AdvancedSettings/CustomModeAdvancedSettings';
import useGameModeSettings from '@/hooks/useGameModeSettings';

const GameModeConfigAdvancedSettings = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const currentGameMode = gameState?.gameMode || GameMode.TIMED;
    const { settings: savedSettings } = useGameModeSettings(currentGameMode);

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;

    const handleSettingsChange = (newSettings: GameModeAdvancedSettings) => {
        if (!isGameMaster || !room) return;

        // Send the advanced settings update to the server
        room.send("updateAdvancedSettings", newSettings);
    };

    // Load saved settings when game mode changes (for game master)
    useEffect(() => {
        if (!isGameMaster || !room || !savedSettings) return;

        // Apply saved settings when switching to a different game mode
        console.log(`🔄 Loading saved settings for ${currentGameMode}:`, savedSettings);
        
        // Send the saved settings to the server to initialize the room
        room.send("updateAdvancedSettings", savedSettings);
    }, [currentGameMode, isGameMaster, room, savedSettings]);

    if (!room || !gameState) return null;

    const renderAdvancedSettings = () => {
        switch (gameState.gameMode) {
            case GameMode.CLASSIC:
                return <ClassicModeAdvancedSettings />;
            case GameMode.TIMED:
                return <TimedModeAdvancedSettings />;
            case GameMode.ENDLESS:
                return <EndlessModeAdvancedSettings />;
            case GameMode.FLASH:
                return <FlashModeAdvancedSettings />;
            case GameMode.HAZARD:
                return <HazardModeAdvancedSettings />;
            case GameMode.FIGHT:
                return <FightModeAdvancedSettings />;
            case GameMode.DAILY:
                return <DailyModeAdvancedSettings />;
            case GameMode.COOP:
                return <CoopModeAdvancedSettings />;
            case GameMode.CUSTOM:
                return <CustomModeAdvancedSettings />;
            default:
                return (
                    <div className="text-center text-shadow-border shadow-black text-gray-400">
                        {t('game.config.advancedSettings.notAvailable')}
                    </div>
                );
        }
    };

    return (
        <GameModeSettingsProvider 
            gameMode={gameState.gameMode} 
            onSettingsChange={handleSettingsChange}
        >
            {renderAdvancedSettings()}
        </GameModeSettingsProvider>
    );
}

export default GameModeConfigAdvancedSettings